import "@/components/tiptap-ui/link-popover/link-popover.scss";
import { Separator } from "@/components/tiptap-ui/separator/separator";
import { UnifiedButton } from "@/components/tiptap-ui/unified-button";
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTurnDownLeft,
  faArrowUpRightFromSquare,
  faEdit,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { BubbleMenu, type Editor } from "@tiptap/react";
import React, { useEffect } from "react";
import { Row } from "../../ui/flex";

interface LinkBubbleMenuProps {
  editor?: Editor | null;
  className?: string;
}

export function LinkBubbleMenu({
  editor: providedEditor,
  className,
}: LinkBubbleMenuProps) {
  const editor = useTiptapEditor(providedEditor);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editUrl, setEditUrl] = React.useState("");

  const currentUrl = editor?.getAttributes("link").href || "";

  // Auto-enter edit mode when link is empty (newly created)
  useEffect(() => {
    if (editor?.isActive("link") && currentUrl === "" && !isEditing) {
      setIsEditing(true);
      setEditUrl("");
    }
  }, [editor, currentUrl, isEditing]);

  if (!editor) {
    return null;
  }

  const handleEdit = () => {
    setEditUrl(currentUrl);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (!editUrl.trim()) {
      handleRemove();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: editUrl })
      .run();

    setIsEditing(false);
    setEditUrl("");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditUrl("");
  };

  const handleRemove = () => {
    editor
      .chain()
      .focus()
      .unsetMark("link", { extendEmptyMarkRange: true })
      .setMeta("preventAutolink", true)
      .run();

    setIsEditing(false);
    setEditUrl("");
  };

  const handleOpenLink = () => {
    if (currentUrl) {
      window.open(currentUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveEdit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      handleCancelEdit();
    }
  };

  return (
    <BubbleMenu
      editor={editor}
      className={`bg-background rounded-full border p-1 shadow-lg ${className || ""}`}
      shouldShow={({ editor }) => {
        // Only show when the cursor is on a link
        return editor.isActive("link");
      }}
      tippyOptions={{
        placement: "bottom-start",
        offset: [0, 8],
      }}
    >
      {isEditing || currentUrl === "" ? (
        <Row align="center">
          <input
            type="url"
            value={editUrl}
            onChange={(e) => setEditUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter URL..."
            autoFocus
            className="clamp block h-10 w-full min-w-[50px] pl-2 outline-none"
          />
          <div>
            <UnifiedButton
              type="button"
              onClick={handleSaveEdit}
              title="Save link"
              data-style="ghost"
            >
              <FontAwesomeIcon icon={faArrowTurnDownLeft} />
            </UnifiedButton>
          </div>
        </Row>
      ) : (
        <Row align="center">
          <a
            className="max-w-48 truncate px-2 text-sm text-blue-600"
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {currentUrl}
          </a>

          <Separator />

          <Row>
            <UnifiedButton
              type="button"
              onClick={handleOpenLink}
              title="Open link"
              disabled={!currentUrl}
              data-style="ghost"
            >
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="tiptap-button-icon"
              />
            </UnifiedButton>

            <UnifiedButton
              type="button"
              onClick={handleEdit}
              title="Edit link"
              data-style="ghost"
            >
              <FontAwesomeIcon icon={faEdit} className="tiptap-button-icon" />
            </UnifiedButton>

            <UnifiedButton
              type="button"
              onClick={handleRemove}
              title="Remove link"
              data-style="ghost"
            >
              <FontAwesomeIcon icon={faTrash} className="tiptap-button-icon" />
            </UnifiedButton>
          </Row>
        </Row>
      )}
    </BubbleMenu>
  );
}
